/**
 * Sidebar Component
 * ==================
 * Left navigation sidebar for the admin portal
 */

import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { usePermissions, useOrg } from '../../hooks';
import { getPendingDrafts } from '../../api/brain';
import { getPendingProposalCount } from '../../api/proposals';

// Module-level cache — survives remounts, only resets on full page reload
let _cachedDraftCount = null;
let _cachedProposalCount = null;

const Sidebar = ({ isCollapsed, onToggle, mobileOpen = false, onMobileClose = () => {} }) => {
  const { can, is, isAdmin, isSuperAdmin } = usePermissions();
  const { isOrgSelected, activeOrg, loading: orgLoading } = useOrg();
  const [pendingDraftCount, setPendingDraftCount] = useState(_cachedDraftCount ?? 0);
  const [pendingProposalCount, setPendingProposalCount] = useState(_cachedProposalCount ?? 0);

  const canAccessBrain = isAdmin || is.complianceOfficer();
  const canAccessUsers = isAdmin || is.complianceOfficer();

  useEffect(() => {
    // Wait until org is fully resolved (switch-org completes and new token is stored)
    if (!isOrgSelected) return;
    // Only fetch once per page load
    if (_cachedDraftCount !== null && _cachedProposalCount !== null) return;

    const fetchCounts = async () => {
      if (canAccessBrain && _cachedDraftCount === null) {
        try {
          const res = await getPendingDrafts('pending', null, 1, 0);
          const count = res.data?.total || 0;
          _cachedDraftCount = count;
          setPendingDraftCount(count);
        } catch {
          _cachedDraftCount = 0;
        }
      }
      if (isAdmin && _cachedProposalCount === null) {
        try {
          const res = await getPendingProposalCount();
          const count = res.data?.count || 0;
          _cachedProposalCount = count;
          setPendingProposalCount(count);
        } catch {
          _cachedProposalCount = 0;
        }
      }
    };
    fetchCounts();
  }, [canAccessBrain, isAdmin, isOrgSelected]);

  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} viewBox="0 0 24 24">
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      ),
      show: true,
    },
    {
      name: 'Leads',
      path: '/leads',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 48 48">
          <rect x="4" y="10" width="40" height="28" rx="3" strokeWidth={2} />
          <circle cx="15" cy="22" r="4.5" strokeWidth={1.5} />
          <path strokeLinecap="round" strokeWidth={2} d="M25 20h14" />
          <path strokeLinecap="round" strokeWidth={1.5} d="M25 25h10" opacity={0.5} />
          <path strokeLinecap="round" strokeWidth={1.5} d="M25 29h7" opacity={0.35} />
          <path strokeLinecap="round" strokeWidth={1.2} d="M10 31h10" opacity={0.3} />
          <path strokeLinecap="round" strokeWidth={1.2} d="M10 34h7" opacity={0.2} />
        </svg>
      ),
      show: can.createLead() || can.readLead(),
    },
    {
      name: 'Meetings',
      path: '/meetings',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      show: can.readMeeting(),
    },
    {
      name: 'Proposals',
      path: '/proposals',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      badge: isAdmin ? pendingProposalCount : 0,
      show: can.readProposal() || can.generateProposal(),
    },
    {
      name: 'Investors',
      path: '/investors',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} viewBox="0 0 24 24">
          <path d="M2 17l4-4 4 4 5-6 5 4" />
          <path d="M18 7h4v4" />
        </svg>
      ),
      show: true,
    },
    {
      name: `${activeOrg?.name || ''} Brain`,
      path: '/brain',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      badge: pendingDraftCount,
      show: canAccessBrain,
    },
    {
      name: 'Users',
      path: '/users',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} viewBox="0 0 24 24">
          <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M22 21v-2a4 4 0 00-3-3.87" />
          <path d="M16 3.13a4 4 0 010 7.75" />
        </svg>
      ),
      show: canAccessUsers && isOrgSelected,
    },
    {
      name: 'Organizations',
      path: '/organizations',
      icon: (
        <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} viewBox="0 0 24 24">
          <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16" />
          <path d="M1 21h22" />
          <path d="M9 7h1M9 11h1M9 15h1M14 7h1M14 11h1M14 15h1" />
        </svg>
      ),
      show: isSuperAdmin,
    },
  ];

  // Pinned to the bottom of the sidebar (above the Version footer)
  const bottomNavItem = {
    name: 'System Prompts',
    path: '/system-prompts',
    icon: (
      <svg className="w-6 h-6 flex-shrink-0" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} viewBox="0 0 24 24">
        <polyline points="4 17 10 11 4 5" />
        <line x1="12" y1="19" x2="20" y2="19" />
      </svg>
    ),
    show: isSuperAdmin,
  };

  const NavItem = ({ item }) => (
    <div className="relative group">
      <NavLink
        to={item.path}
        onClick={onMobileClose}
        className={({ isActive }) =>
          `flex items-center flex-nowrap ${isCollapsed ? 'justify-center px-0' : 'gap-3 px-4'} py-3 rounded-lg transition-all duration-200 ${
            isActive
              ? 'bg-primary/15 dark:bg-primary/15 text-primary'
              : 'text-[#262A2E] dark:text-[#B5B5B5] hover:translate-x-1'
          }`
        }
      >
        <span className="relative flex-shrink-0">
          {item.icon}
          {/* Badge dot when sidebar is collapsed */}
          {isCollapsed && item.badge > 0 && (
            <span className="absolute -top-1.5 -right-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-primary-50 border border-primary-200 px-1 text-[10px] font-semibold text-primary leading-none">
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </span>
        {!isCollapsed && (
          <>
            <span className="text-sm flex-1 whitespace-nowrap">{item.name}</span>
            {item.badge > 0 && (
              <span className="ml-auto flex-shrink-0 flex h-[22px] min-w-[22px] items-center justify-center rounded-md bg-primary-50 border border-primary-200 px-1.5 text-[11px] font-semibold text-primary leading-none">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            )}
          </>
        )}
      </NavLink>

      {/* Tooltip shown only when collapsed */}
      {isCollapsed && (
        <span className="absolute left-full top-1/2 -translate-y-1/2 ml-3 px-2 py-1 bg-[#242628] text-white text-xs rounded whitespace-nowrap z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
          {item.name}
          {item.badge > 0 && ` (${item.badge})`}
        </span>
      )}
    </div>
  );

  return (
    <>
    {/* Mobile backdrop — tap to dismiss the drawer */}
    {mobileOpen && (
      <div
        className="md:hidden fixed inset-0 z-40 bg-black/50"
        onClick={onMobileClose}
        aria-hidden="true"
      />
    )}
    <aside
      role={mobileOpen ? 'dialog' : undefined}
      aria-modal={mobileOpen ? true : undefined}
      aria-label={mobileOpen ? 'Navigation menu' : undefined}
      className={`fixed left-0 top-0 flex bg-white dark:bg-[#242628] border-r border-[#F0F0F0] dark:border-[#3A3C3E] h-screen flex-col z-50 overflow-y-auto overflow-x-visible transition-all duration-300 w-64 ${
        isCollapsed ? 'md:w-16' : 'md:w-64'
      } ${mobileOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0`}
    >
      {/* Logo Section */}
      <div className={`flex items-center h-[65px] ${isCollapsed ? 'justify-center px-3' : 'justify-between px-6'}`}>
        {!isCollapsed && (
          <div className="flex items-center flex-1 min-w-0">
            {!activeOrg && orgLoading ? (
              // Still resolving the active org — reserve the logo height instead of flashing the
              // default Zamanat logo (which would swap to the org logo and cause a flicker).
              <div className="h-10" aria-hidden="true" />
            ) : !activeOrg ? (
              <>
                {/* <img
                  src={process.env.PUBLIC_URL + '/images/logo.svg'}
                  alt="Zamanat Logo"
                  className="h-10 w-auto dark:hidden"
                /> */}
                {/* <img
                  src={process.env.PUBLIC_URL + '/images/logo-white.svg'}
                  alt="Zamanat Logo"
                  className="h-10 w-auto hidden dark:block"
                /> */}
              </>
            ) : activeOrg.logo_url ? (
              <img
                src={activeOrg.logo_url}
                alt={activeOrg.name}
                className="h-10 w-auto max-w-[140px] object-contain"
              />
            ) : (
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate max-w-[140px]">
                {activeOrg.name}
              </span>
            )}
          </div>
        )}

        {isCollapsed && (
          <button
            onClick={onToggle}
            type="button"
            className="flex items-center justify-center cursor-pointer"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            {!activeOrg && orgLoading ? (
              // Reserve the icon footprint while resolving — avoids the default-logo flicker.
              <div className="h-8 w-8" aria-hidden="true" />
            ) : !activeOrg ? (
              <img
                src={process.env.PUBLIC_URL + '/images/logo-icon.svg'}
                alt="Zamanat Logo"
                className="h-8 w-8"
              />
            ) : activeOrg.favicon_url || activeOrg.logo_url ? (
              // Prefer the square favicon for the collapsed icon; the wide
              // logo_url is only a fallback (it gets squished into 8x8).
              <img
                src={activeOrg.favicon_url || activeOrg.logo_url}
                alt={activeOrg.name}
                className="h-8 w-8 object-contain rounded"
              />
            ) : (
              <div className="h-8 w-8 rounded bg-purple-600 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold text-sm">
                  {activeOrg.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </button>
        )}

        {/* Toggle button — only when expanded (desktop only) */}
        {!isCollapsed && (
          <button
            onClick={onToggle}
            type="button"
            className="hidden md:flex items-center justify-center w-7 h-7 text-gray-900 dark:text-white transition-colors flex-shrink-0"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <line x1="10" y1="12" x2="20" y2="12" />
              <line x1="10" y1="12" x2="14" y2="16" />
              <line x1="10" y1="12" x2="14" y2="8" />
              <line x1="4" y1="4" x2="4" y2="20" />
            </svg>
          </button>
        )}

        {/* Close button — mobile drawer only */}
        <button
          onClick={onMobileClose}
          type="button"
          className="md:hidden flex items-center justify-center w-8 h-8 text-gray-900 dark:text-white transition-colors flex-shrink-0"
          aria-label="Close navigation menu"
        >
          <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      </div>

      {/* Navigation */}
      <nav className={`flex-1 flex flex-col py-4 ${isCollapsed ? 'px-2' : 'px-4'}`}>
        <div className="space-y-1">
          {navItems.map((item) => {
            if (!item.show) return null;
            return <NavItem key={item.path} item={item} />;
          })}
        </div>
        {/* Bottom-pinned item(s), above the Version footer */}
        {bottomNavItem.show && (
          <div className="mt-auto pt-1">
            <NavItem item={bottomNavItem} />
          </div>
        )}
      </nav>

      {/* Footer */}
      {!isCollapsed && (
        <div className="p-4 border-t border-[#F0F0F0] dark:border-[#3A3C3E]">
          <div className="text-xs text-[#90A4AE] dark:text-[#6E7278] text-center">
            Version {process.env.REACT_APP_VERSION || '1.0.0'}
          </div>
        </div>
      )}
    </aside>
    </>
  );
};

export default Sidebar;
